/**
 * MergeChangesDialog Component
 *
 * Dialog for resolving conflicts when both local and remote changes occur
 * simultaneously. Provides different merge strategies and field-by-field
 * resolution options.
 *
 * @author Claude Code
 * @date 2025-09-22
 */

import React, { useState, useMemo, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Chip,
  RadioGroup,
  FormControlLabel,
  Radio,
  Card,
  CardContent,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  IconButton,
  Tooltip,
  useTheme,
  alpha,
  Accordion,
  AccordionSummary,
  AccordionDetails
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  CompareArrows as CompareArrowsIcon,
  Check as CheckIcon,
  Close as CloseIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  AutoFixHigh as AutoFixHighIcon
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { FieldChange, MergeStrategy } from '../../types';

// Styled components
const ConflictCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.warning.main}`,
  backgroundColor: alpha(theme.palette.warning.main, 0.05),
}));

const ChangePreview = styled(Box)<{ selected: boolean }>(({ theme, selected }) => ({
  padding: theme.spacing(1.5),
  borderRadius: theme.shape.borderRadius,
  border: `2px solid ${selected ? theme.palette.primary.main : theme.palette.divider}`,
  backgroundColor: selected
    ? alpha(theme.palette.primary.main, 0.08)
    : theme.palette.background.paper,
  cursor: 'pointer',
  transition: theme.transitions.create(['border-color', 'background-color'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    borderColor: selected ? theme.palette.primary.main : theme.palette.text.primary,
    backgroundColor: selected
      ? alpha(theme.palette.primary.main, 0.12)
      : alpha(theme.palette.action.hover, 0.04),
  },
}));

const SourceChip = styled(Chip)<{ source: 'local' | 'remote' | 'merged' }>(({ theme, source }) => ({
  backgroundColor: source === 'local'
    ? theme.palette.success.main
    : source === 'remote'
    ? theme.palette.info.main
    : theme.palette.secondary.main,
  color: theme.palette.getContrastText(
    source === 'local'
      ? theme.palette.success.main
      : source === 'remote'
      ? theme.palette.info.main
      : theme.palette.secondary.main
  ),
  fontSize: '0.75rem',
  height: '20px',
}));

// Types
export interface MergeConflict {
  path: string;
  fieldName: string;
  localValue: any;
  remoteValue: any;
  localChange: FieldChange;
  remoteChange: FieldChange;
  autoMergeAvailable: boolean;
  autoMergedValue?: any;
  description?: string;
}

export interface MergeChangesDialogProps {
  open: boolean;
  onClose: () => void;
  conflicts: MergeConflict[];
  onResolve: (resolutions: Record<string, { strategy: MergeStrategy; value: any }>) => void;
  onCancel: () => void;
  title?: string;
  subtitle?: string;
}

/**
 * Dialog for resolving merge conflicts
 */
export const MergeChangesDialog: React.FC<MergeChangesDialogProps> = ({
  open,
  onClose,
  conflicts,
  onResolve,
  onCancel,
  title = 'Разрешение конфликтов данных',
  subtitle = 'Обнаружены одновременные изменения. Выберите, какие изменения сохранить.'
}) => {
  const theme = useTheme();

  // State for conflict resolutions
  const [resolutions, setResolutions] = useState<Record<string, {
    strategy: MergeStrategy;
    value: any;
  }>>({});

  // Global merge strategy
  const [globalStrategy, setGlobalStrategy] = useState<MergeStrategy>('manual');

  // Initialize resolutions when conflicts change
  React.useEffect(() => {
    const initialResolutions: Record<string, { strategy: MergeStrategy; value: any }> = {};

    conflicts.forEach(conflict => {
      if (conflict.autoMergeAvailable) {
        initialResolutions[conflict.path] = {
          strategy: 'smart',
          value: conflict.autoMergedValue
        };
      } else {
        initialResolutions[conflict.path] = {
          strategy: 'keep-local',
          value: conflict.localValue
        };
      }
    });

    setResolutions(initialResolutions);
  }, [conflicts]);

  // Apply global strategy
  const handleGlobalStrategyChange = useCallback((strategy: MergeStrategy) => {
    setGlobalStrategy(strategy);

    const newResolutions: Record<string, { strategy: MergeStrategy; value: any }> = {};

    conflicts.forEach(conflict => {
      switch (strategy) {
        case 'keep-local':
          newResolutions[conflict.path] = {
            strategy: 'keep-local',
            value: conflict.localValue
          };
          break;
        case 'take-remote':
          newResolutions[conflict.path] = {
            strategy: 'take-remote',
            value: conflict.remoteValue
          };
          break;
        case 'smart':
          newResolutions[conflict.path] = {
            strategy: 'smart',
            value: conflict.autoMergeAvailable
              ? conflict.autoMergedValue
              : conflict.localValue
          };
          break;
        case 'manual':
          // Keep existing resolutions for manual strategy
          if (!resolutions[conflict.path]) {
            newResolutions[conflict.path] = {
              strategy: 'keep-local',
              value: conflict.localValue
            };
          }
          break;
      }
    });

    if (strategy !== 'manual') {
      setResolutions(newResolutions);
    }
  }, [conflicts, resolutions]);

  // Update individual conflict resolution
  const handleConflictResolution = useCallback((
    path: string,
    strategy: MergeStrategy,
    value: any
  ) => {
    setResolutions(prev => ({
      ...prev,
      [path]: { strategy, value }
    }));
  }, []);

  // Format value for display
  const formatValue = (value: any): string => {
    if (value === null || value === undefined) return 'Не задано';
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return value.toString();
    if (typeof value === 'boolean') return value ? 'Да' : 'Нет';
    if (Array.isArray(value)) return `${value.length} элементов`;
    if (typeof value === 'object') return JSON.stringify(value, null, 2);
    return String(value);
  };

  // Get source icon
  const getSourceIcon = (source: string) => {
    switch (source) {
      case 'firebase': return '🔄';
      case 'user': return '👤';
      case 'system': return '⚙️';
      default: return '📝';
    }
  };

  // Check if all conflicts are resolved
  const allConflictsResolved = conflicts.every(conflict =>
    resolutions[conflict.path] !== undefined
  );

  // Handle resolve
  const handleResolve = () => {
    onResolve(resolutions);
    onClose();
  };

  // Handle cancel
  const handleCancel = () => {
    onCancel();
    onClose();
  };

  // Calculate statistics
  const stats = useMemo(() => {
    const total = conflicts.length;
    const autoMergeable = conflicts.filter(c => c.autoMergeAvailable).length;
    const localWins = Object.values(resolutions).filter(r => r.strategy === 'keep-local').length;
    const remoteWins = Object.values(resolutions).filter(r => r.strategy === 'take-remote').length;
    const smartMerges = Object.values(resolutions).filter(r => r.strategy === 'smart').length;

    return { total, autoMergeable, localWins, remoteWins, smartMerges };
  }, [conflicts, resolutions]);

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          maxHeight: '80vh',
          minHeight: '60vh',
        }
      }}
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <WarningIcon color="warning" />
          <Box>
            <Typography variant="h6">
              {title}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {subtitle}
            </Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent>
        {/* Statistics */}
        <Box mb={3}>
          <Typography variant="subtitle2" gutterBottom>
            Статистика конфликтов
          </Typography>
          <Box display="flex" gap={1} flexWrap="wrap">
            <Chip
              size="small"
              label={`Всего: ${stats.total}`}
              color="primary"
            />
            <Chip
              size="small"
              label={`Авто-слияние: ${stats.autoMergeable}`}
              color="secondary"
            />
            <Chip
              size="small"
              label={`Локальные: ${stats.localWins}`}
              color="success"
            />
            <Chip
              size="small"
              label={`Удалённые: ${stats.remoteWins}`}
              color="info"
            />
            <Chip
              size="small"
              label={`Умные: ${stats.smartMerges}`}
              color="default"
            />
          </Box>
        </Box>

        {/* Global Strategy Selection */}
        <Accordion defaultExpanded>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="subtitle1">
              Глобальная стратегия разрешения
            </Typography>
          </AccordionSummary>
          <AccordionDetails>
            <RadioGroup
              value={globalStrategy}
              onChange={(e) => handleGlobalStrategyChange(e.target.value as MergeStrategy)}
            >
              <FormControlLabel
                value="keep-local"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      Сохранить локальные изменения
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Все ваши изменения будут сохранены
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="take-remote"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      Принять удалённые изменения
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Все внешние изменения будут приняты
                    </Typography>
                  </Box>
                }
              />
              <FormControlLabel
                value="smart"
                control={<Radio />}
                label={
                  <Box display="flex" alignItems="center" gap={0.5}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        Умное слияние
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Автоматическое слияние, где возможно
                      </Typography>
                    </Box>
                    <AutoFixHighIcon fontSize="small" color="secondary" />
                  </Box>
                }
              />
              <FormControlLabel
                value="manual"
                control={<Radio />}
                label={
                  <Box>
                    <Typography variant="body2" fontWeight="medium">
                      Ручное разрешение
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      Выбрать для каждого поля отдельно
                    </Typography>
                  </Box>
                }
              />
            </RadioGroup>
          </AccordionDetails>
        </Accordion>

        {/* Individual Conflicts */}
        {globalStrategy === 'manual' && (
          <Box mt={3}>
            <Typography variant="subtitle1" gutterBottom>
              Конфликтующие поля
            </Typography>

            {conflicts.map((conflict, index) => {
              const resolution = resolutions[conflict.path];

              return (
                <ConflictCard key={conflict.path}>
                  <CardContent>
                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                      <Box>
                        <Typography variant="subtitle2" fontWeight="medium">
                          {conflict.fieldName}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {conflict.path}
                        </Typography>
                      </Box>
                      {conflict.autoMergeAvailable && (
                        <Tooltip title="Доступно автоматическое слияние">
                          <AutoFixHighIcon color="secondary" fontSize="small" />
                        </Tooltip>
                      )}
                    </Box>

                    {conflict.description && (
                      <Typography variant="body2" color="text.secondary" mb={2}>
                        {conflict.description}
                      </Typography>
                    )}

                    {/* Local Value */}
                    <ChangePreview
                      selected={resolution?.strategy === 'keep-local'}
                      onClick={() => handleConflictResolution(
                        conflict.path,
                        'keep-local',
                        conflict.localValue
                      )}
                      mb={1}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <SourceChip
                            source="local"
                            size="small"
                            label={`${getSourceIcon(conflict.localChange.source)} Ваши изменения`}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(conflict.localChange.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                        {resolution?.strategy === 'keep-local' && (
                          <CheckIcon color="primary" fontSize="small" />
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        {formatValue(conflict.localValue)}
                      </Typography>
                    </ChangePreview>

                    {/* Remote Value */}
                    <ChangePreview
                      selected={resolution?.strategy === 'take-remote'}
                      onClick={() => handleConflictResolution(
                        conflict.path,
                        'take-remote',
                        conflict.remoteValue
                      )}
                      mb={1}
                    >
                      <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                        <Box display="flex" alignItems="center" gap={1}>
                          <SourceChip
                            source="remote"
                            size="small"
                            label={`${getSourceIcon(conflict.remoteChange.source)} Внешние изменения`}
                          />
                          <Typography variant="caption" color="text.secondary">
                            {new Date(conflict.remoteChange.timestamp).toLocaleString()}
                          </Typography>
                        </Box>
                        {resolution?.strategy === 'take-remote' && (
                          <CheckIcon color="primary" fontSize="small" />
                        )}
                      </Box>
                      <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                        {formatValue(conflict.remoteValue)}
                      </Typography>
                    </ChangePreview>

                    {/* Auto-merged Value */}
                    {conflict.autoMergeAvailable && (
                      <ChangePreview
                        selected={resolution?.strategy === 'smart'}
                        onClick={() => handleConflictResolution(
                          conflict.path,
                          'smart',
                          conflict.autoMergedValue
                        )}
                      >
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                          <Box display="flex" alignItems="center" gap={1}>
                            <SourceChip
                              source="merged"
                              size="small"
                              label="🤖 Автослияние"
                            />
                            <Typography variant="caption" color="text.secondary">
                              Рекомендуемое
                            </Typography>
                          </Box>
                          {resolution?.strategy === 'smart' && (
                            <CheckIcon color="primary" fontSize="small" />
                          )}
                        </Box>
                        <Typography variant="body2" sx={{ wordBreak: 'break-all' }}>
                          {formatValue(conflict.autoMergedValue)}
                        </Typography>
                      </ChangePreview>
                    )}
                  </CardContent>
                </ConflictCard>
              );
            })}
          </Box>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={handleCancel} color="inherit">
          Отменить
        </Button>
        <Button
          onClick={handleResolve}
          variant="contained"
          color="primary"
          disabled={!allConflictsResolved}
        >
          Разрешить конфликты
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default MergeChangesDialog;